// src/pages/common-components/Branches/BranchesDrawer.tsx
// "Our Branches" as a right-hand slide-in drawer (same overlay/dialog pattern
// as the "Book a Test Drive" drawer — see ReserveCarModal) instead of a full
// page, so visitors can check locations/hours without leaving the page they're on.
import React, { useEffect, useState } from 'react';
import { BranchService, Branch, DAYS_OF_WEEK, DAY_LABELS, formatTime12h } from '../../Services/branchService';

interface BranchesDrawerProps {
  onClose: () => void;
}

const styles = `
  .brd-overlay {
    position: fixed; inset: 0; z-index: 9999; background: rgba(10,8,6,0.5);
    animation: brd-fade-in 0.25s ease;
  }
  .brd-dialog {
    background: #fff; width: 100%; max-width: 420px; position: fixed;
    top: 0; left: 0; height: 100%; height: 100dvh;
    display: flex; flex-direction: column; box-shadow: 8px 0 24px rgba(0,0,0,0.15);
    animation: brd-slide-in 0.28s cubic-bezier(0.16, 1, 0.3, 1);
  }
  @keyframes brd-fade-in { from { opacity: 0; } to { opacity: 1; } }
  @keyframes brd-slide-in { from { transform: translateX(-100%); } to { transform: translateX(0); } }
  @media (max-width: 480px) {
    .brd-dialog { max-width: 100%; }
  }

  .brd-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px; border-bottom: 1px solid #eee; flex-shrink: 0;
  }
  .brd-title { font-size: 18px; font-weight: 600; margin: 0; }
  .brd-subtitle { font-size: 13px; color: #777; margin: 0 0 18px; }
  .brd-close {
    background: #f0f0f5; border: none; border-radius: 50%; width: 38px; height: 38px;
    display: flex; align-items: center; justify-content: center;
    font-size: 20px; cursor: pointer; color: #6b7280; line-height: 1; flex-shrink: 0;
    transition: all 0.25s ease;
  }
  .brd-close:hover { background: #717fe0; color: #fff; transform: rotate(90deg); }

  .brd-body { padding: 20px; overflow-y: auto; flex: 1 1 auto; }
  .brd-state { text-align: center; padding: 40px 0; color: #777; font-size: 14px; }
  .brd-error { color: #c0392b; }

  .brd-card {
    border: 1px solid #e8e8e8; border-radius: 6px; padding: 16px 18px;
    margin-bottom: 14px; transition: border-color 0.2s, box-shadow 0.2s;
  }
  .brd-card:hover { border-color: #c8cef5; }
  .brd-card-name { font-size: 15px; font-weight: 600; color: #333; margin: 0 0 6px; }
  .brd-card-address { font-size: 13px; color: #777; line-height: 1.6; margin: 0 0 6px; display: flex; align-items: flex-start; gap: 6px; }
  .brd-card-address i { margin-top: 2px; flex-shrink: 0; }
  .brd-card-phone { font-size: 13px; color: #444; margin: 0 0 4px; display: flex; align-items: center; gap: 6px; }

  .brd-hours-toggle {
    width: 100%; display: flex; align-items: center; justify-content: space-between;
    gap: 10px; padding: 10px 0 0; margin-top: 8px; border: none; border-top: 1px solid #f0f0f0;
    background: none; cursor: pointer; text-align: left; font-family: inherit;
    font-size: 13px; font-weight: 500; color: #333;
  }
  .brd-hours-toggle:hover { color: #717fe0; }
  .brd-chevron { font-size: 17px; color: #bbb; flex-shrink: 0; transition: transform 0.25s ease, color 0.2s; }
  .brd-chevron--up { transform: rotate(180deg); color: #717fe0; }

  .brd-hours-body { padding-top: 10px; animation: brd-hours-slide 0.2s ease; }
  @keyframes brd-hours-slide { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }
  .brd-hours-row {
    display: flex; justify-content: space-between; padding: 4px 0;
    border-bottom: 1px solid #f5f5f5; font-size: 13px;
  }
  .brd-hours-row:last-child { border-bottom: none; }
  .brd-hours-row span:first-child { color: #777; }
  .brd-hours-row span:last-child { color: #333; }
`;

// Address lines, opening-hours rows, etc. don't change once a branch's data
// is loaded — collapsing hours per-branch by id keeps every card independent.
const BranchesDrawer: React.FC<BranchesDrawerProps> = ({ onClose }) => {
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await BranchService.getDisplayedBranches();
        if (mounted) setBranches(data);
      } catch (err) {
        console.error('Error loading branches:', err);
        if (mounted) setError('Failed to load branches. Please try again later.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  // Lock body scroll while the drawer overlay is open — same as ReserveCarModal.
  useEffect(() => {
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = scrollbarWidth + 'px';
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    };
  }, []);

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <>
      <style>{styles}</style>
      <div className="brd-overlay" onClick={onClose}>
        <div className="brd-dialog" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="brd-header">
            <h3 className="brd-title">Our Branches</h3>
            <button className="brd-close" onClick={onClose} aria-label="Close">&times;</button>
          </div>

          <div className="brd-body">
            <p className="brd-subtitle">Locations, contact details, and opening hours.</p>

            {loading && <div className="brd-state">Loading branches…</div>}
            {!loading && error && <div className="brd-state brd-error">{error}</div>}
            {!loading && !error && branches.length === 0 && (
              <div className="brd-state">No branches are currently listed. Please check back soon.</div>
            )}

            {!loading && !error && branches.map((branch) => {
              const addressLine = [
                branch.address.street,
                branch.address.city,
                branch.address.state,
                branch.address.country,
              ].filter(Boolean).join(', ');
              const isExpanded = expandedIds.has(branch.id);

              return (
                <div key={branch.id} className="brd-card">
                  <h4 className="brd-card-name">{branch.name}</h4>
                  {addressLine && (
                    <p className="brd-card-address">
                      <i className="zmdi zmdi-pin"></i>
                      <span>{addressLine}</span>
                    </p>
                  )}
                  {branch.contact?.phone && (
                    <p className="brd-card-phone">
                      <i className="zmdi zmdi-phone"></i>
                      {branch.contact.phone}
                    </p>
                  )}

                  {branch.operatingHours && (
                    <>
                      <button
                        className="brd-hours-toggle"
                        onClick={() => toggleExpanded(branch.id)}
                        type="button"
                      >
                        <span>Opening Hours</span>
                        <i className={`zmdi zmdi-chevron-down brd-chevron ${isExpanded ? 'brd-chevron--up' : ''}`}></i>
                      </button>

                      {isExpanded && (
                        <div className="brd-hours-body">
                          {DAYS_OF_WEEK.map((day) => {
                            const dayHours = branch.operatingHours?.[day];
                            return (
                              <div key={day} className="brd-hours-row">
                                <span>{DAY_LABELS[day]}</span>
                                <span>
                                  {dayHours
                                    ? (dayHours.closed
                                        ? 'Closed'
                                        : `${formatTime12h(dayHours.open)} – ${formatTime12h(dayHours.close)}`)
                                    : 'Not set'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default BranchesDrawer;
